import {bannerWidthToHeightRatio, InputFileAcceptMimes, InputFileAcceptTypes, UploadStates} from "../../../../../core/constants/enums";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/dropzone/image-placeholder.svg";
import DropZone from "../../../base/dropzone";
import LazyImage from "../../lazy-image";
import ObserversService from "../../../../../core/services/observers";
import Utils, {createUUId, readFile, ReadingFileAs} from "../../../../../core/services/utils/utils";
import classnames from "classnames";
import ToastService from "../../../../../core/services/toast";


const AddBannerImageDropZone = ({
                                    disabled,
                                    widthToHeightRatio = bannerWidthToHeightRatio,
                                    accept = [
                                        InputFileAcceptMimes.images,
                                        InputFileAcceptTypes.heic,
                                        InputFileAcceptTypes.heif,
                                    ],
                                    onSuccess,
                                    uploadState,
                                    url,
                                    id,
                                }) => {
    const idRef = useRef(createUUId(true));
    const [loading, setLoading] = useState(false);
    const [height, setHeight] = useState(200);
    const [tempUrl, setTempUrl] = useState();

    const uploading = useMemo(() => uploadState?.state === UploadStates.pending, [uploadState]);

    const correctAspectRatio = useMemo(() => {
        const [correctWidthAp, correctHeightAp] = Utils.getAspectRatio(widthToHeightRatio, 1);
        return `${correctWidthAp}:${correctHeightAp}`;
    }, [widthToHeightRatio])

    const exampleImageDimension = useMemo(() => {
        const [wAp, hAp] = Utils.getAspectRatio(widthToHeightRatio, 1);
        const mul = Math.max(Math.ceil(1000 / wAp), 1);
        return `${wAp * mul}px * ${hAp * mul}px`;
    }, [widthToHeightRatio])


    /**
     * With each change in the url of the dropzone:
     * - resets the value of temp url.
     */
    useEffect(() => {
        setTempUrl(null);
    }, [url])

    /**
     * As soon as the component mounts:
     * - attaches a resize observer to change the height of the banner with respect to the available width so that
     * the aspect ratio of the banner gets preserved.
     */
    useEffect(() => {
        const observer = ObserversService.newResizeObserver(calculateHeight);
        ObserversService.observeResizeObserver(observer, document.getElementById(id))
        calculateHeight();
        return () => {
            ObserversService.disconnectResizeObserver(observer);
        }
    }, [id])

    /**
     * Calculates the height of the banner image based on the image of the
     */
    const calculateHeight = () => {
        const bannerEl = document.getElementById(id);
        setHeight(bannerEl.getBoundingClientRect().width / bannerWidthToHeightRatio);
    }

    /**
     * Invokes the appropriate controller callback for the adding a new content to a component.
     * @param files {File[] | null}
     */
    const onFileSelected = async (files) => {
        const data = files[0];
        const dataUrl = await readFile(data, ReadingFileAs.dataUrl);
        if (!(await doesImageHaveCorrectAspectRatio(dataUrl)))
            return;
        setTempUrl(dataUrl);
        const res = {
            contentBytes: btoa(await readFile(data, ReadingFileAs.binaryString)),
            fileName: Utils.sanitizeFileName(data.name),
        }
        onSuccess(res);
    }

    /**
     * Determines whether the given image has correct aspect ratio for the banners of the application.
     * @param {string} dataUrl
     * @return {Promise<unknown>}
     */
    const doesImageHaveCorrectAspectRatio = (dataUrl) => {
        const image = new Image();
        image.src = dataUrl;
        return new Promise(r => {
            image.onload = () => {
                const [widthAp, heightAp] = Utils.getAspectRatio(image.naturalWidth, image.naturalHeight);
                const aspectRatio = `${widthAp}:${heightAp}`;
                const res = aspectRatio === correctAspectRatio;
                if (!res)
                    ToastService.error({
                        title: "Wrong aspect ratio",
                        message: `The selected image has an aspect ratio of ${aspectRatio} while banners of the application must` +
                            ` have a aspect ratio of ${correctAspectRatio}`
                    })
                r(res);
            }
        });

    }

    return (
        <>
            <DropZone
                id={idRef.current}
                style={{
                    '--height': `${height}px`,
                }}
                disabled={disabled}
                identifier={'banner-image'}
                accept={accept}
                onSuccess={onFileSelected}
                multiple={false}
                loading={loading}
                uploading={uploading}
                setLoading={setLoading}
                openOnClick={!disabled}
                className={`row no-gutters align-items-center justify-content-center `}
                noParse>
                {() => uploading
                    ? <div
                        className={'pending-content-container'}>
                        <p className={classnames(
                            'progress-percentage',
                            {'red': uploadState.state === UploadStates.error,}
                        )}>
                            {
                                uploading
                                    ? ((uploadState.percentage ?? 0) + '%')
                                    : uploadState.state
                            }
                        </p>
                        {
                            tempUrl?.length > 0
                                ? <img
                                    alt={''}
                                    src={tempUrl}
                                    style={{
                                        opacity: uploadState.state === UploadStates.done ? '1' : '0.5'
                                    }}
                                />
                                : <div className='background-grey-1 cursor-progress-hover rounded-xxl'
                                       style={{
                                           width: '100px',
                                           height: '100px',
                                           opacity: uploadState.state === UploadStates.done ? '1' : '0.5'
                                       }}
                                />
                        }
                    </div>
                    : <>
                        <Row
                            className={`w-100 no-gutters align-items-center justify-content-center ${uploading ? 'cursor-progress-hover' : ''} ${disabled || loading ? 'cursor-not-allowed-hover' : 'cursor-pointer-hover'}`}>
                            <Col xs={12} className={'d-flex justify-content-center align-items-center'}>
                                {
                                    tempUrl?.length > 0
                                        ? <img
                                            alt={'preview'}
                                            src={tempUrl}
                                        />
                                        : url?.length > 0
                                            ? <LazyImage
                                                className='cursor-pointer-hover'
                                                src={url}
                                            />
                                            : <ImagePlaceholder className={'placeholder-image'}/>
                                }
                            </Col>
                            <Col xs={12}
                                 className={'d-flex flex-column justify-content-center align-items-center'}>
                                <p className={'placeholder-text'}>
                                    Drag Your File or Click Here to Upload Your Image <br/>
                                    The image must have a {correctAspectRatio} aspect ratio
                                    (i.e. {exampleImageDimension})
                                </p>
                            </Col>
                        </Row>
                    </>
                }
            </DropZone>
        </>


    );
}

export default AddBannerImageDropZone;
